import { DOCUMENT, effect, inject, Service } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, tap } from 'rxjs';
import { GeneralSettingsService } from './general-settings.service';

/**
 * «روشن نگه داشتن صفحه‌نمایش» — holds a Screen Wake Lock while the setting is on,
 * so the device does not dim and lock itself out from under a trader who is
 * watching the market rather than touching the screen.
 *
 * Two things about the platform shape this:
 *
 * - The browser releases the lock whenever the page is hidden — a tab switch, the
 *   app going to the background, the screen locking by hand. It is not handed back
 *   on return, so the lock is re-taken on `visibilitychange`; without that the
 *   setting silently stops working after the first time the trader leaves the app.
 * - `navigator.wakeLock` is absent on desktop Safari and on every browser without
 *   a secure context. A missing API is not an error worth reporting: the setting
 *   simply has nothing to act on, and the request is allowed to fail quietly.
 * - `request()` is asynchronous, so transitions are queued rather than run as they
 *   arrive. Overlapping them loses: a release that runs while a request is still in
 *   flight finds nothing to release, and the request then pins a lock the setting
 *   has already said to drop. That is not a rare interleaving — `noSleep` defaults
 *   to on, so a trader who stored it *off* produces exactly this sequence on every
 *   cold start, as the defaults publish before the API answers.
 *
 * Instantiated at startup (see `EVENT_DRIVEN_STARTUP`) rather than by a screen, so
 * the lock follows the setting for as long as the app is open.
 */
@Service()
export class ScreenWakeService {
	private readonly doc = inject(DOCUMENT);
	private readonly generalSettings = inject(GeneralSettingsService);

	/** The lock currently held, or null when none is — including after the browser dropped it. */
	private sentinel: WakeLockSentinel | null = null;

	/** Tail of the transition queue; see the note on overlapping requests above. */
	private transitions: Promise<unknown> = Promise.resolve();

	constructor() {
		effect(() => {
			this.sync(this.generalSettings.keepScreenAwake());
		});

		toSignal(
			fromEvent(this.doc, 'visibilitychange').pipe(
				tap(() => {
					if (this.doc.visibilityState === 'visible') {
						this.sync(this.generalSettings.keepScreenAwake(), { sentinelWasDropped: true });
					}
				})
			)
		);
	}

	/**
	 * Bring the held lock in line with the setting, behind whatever transition is
	 * already running. Safe to call when already in line.
	 *
	 * Queued rather than immediate so the last value the setting took is the one the
	 * device ends up on: each transition sees the sentinel the previous one left.
	 */
	private sync(keepAwake: boolean, { sentinelWasDropped = false } = {}): void {
		this.transitions = this.transitions
			.catch(() => undefined)
			.then(() => {
				// Coming back from hidden: the browser released whatever we held, so the
				// sentinel we are still holding names a dead lock. Forgotten inside the
				// queue rather than at the event, so it cannot clear one that a
				// still-running transition is about to store.
				if (sentinelWasDropped) {
					this.sentinel = null;
				}

				return keepAwake ? this.acquire() : this.release();
			});
	}

	private async acquire(): Promise<void> {
		if (this.sentinel && !this.sentinel.released) {
			return;
		}

		// A hidden page cannot take a lock; the visibility listener retries on return.
		if (!('wakeLock' in navigator) || this.doc.visibilityState !== 'visible') {
			return;
		}

		try {
			this.sentinel = await navigator.wakeLock.request('screen');
		} catch {
			// Denied, unsupported, or the page went away mid-request. Nothing to report:
			// the setting is a preference, not a promise the hardware has to keep.
			this.sentinel = null;
		}
	}

	private async release(): Promise<void> {
		const sentinel = this.sentinel;
		this.sentinel = null;

		if (!sentinel || sentinel.released) {
			return;
		}

		try {
			await sentinel.release();
		} catch {
			// Already gone — the browser released it first.
		}
	}
}
